import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, Activity, Zap, Shield, Microscope, Dna, Wind } from 'lucide-react';

interface Module {
  id: string;
  title: string;
  icon: React.ElementType;
  color: string;
  description: string;
  biomarkers: string[];
  status: 'active' | 'coming-soon';
  launchDate?: string;
  includedIn: string;
}

const testingModules: Module[] = [
  {
    id: 'cardiometabolic',
    title: 'Cardiometabolic',
    icon: Heart,
    color: 'red',
    description: 'Complete cardiovascular and metabolic health assessment',
    biomarkers: [
      'Apolipoprotein B (ApoB), LDL Particle Number (LDL-P), small-dense LDL (sdLDL)',
      'Apolipoprotein A1 (ApoA1), Lipoprotein(a) [Lp(a)]',
      'High-Sensitivity C-Reactive Protein (hs-CRP), Homocysteine',
      'HbA1c, Fasting Insulin, C-Peptide, GlycMark®',
      'Omega-3 Index, Omega-6/Omega-3 Ratio, Trans Fat Index',
      'Fibrinogen, Uric Acid'
    ],
    status: 'active',
    includedIn: 'Core Labs OS'
  },
  {
    id: 'hormone',
    title: 'Hormone Optimization',
    icon: Activity,
    color: 'purple',
    description: 'Comprehensive sex hormone and thyroid analysis',
    biomarkers: [
      'Total Testosterone, Free Testosterone (Calculated)',
      'Estradiol (Sensitive), Progesterone, DHEA-S',
      'Sex Hormone Binding Globulin (SHBG)',
      'Luteinizing Hormone (LH), Follicle Stimulating Hormone (FSH)',
      'TSH, Free T3, Free T4, Reverse T4, TPO Antibodies',
      'Cortisol (4-point Diurnal Rhythm), Pregnenolone'
    ],
    status: 'active',
    includedIn: 'Performance OS'
  },
  {
    id: 'nutrient',
    title: 'Nutrient Status',
    icon: Zap,
    color: 'green',
    description: 'Essential vitamin and mineral status assessment',
    biomarkers: [
      'Vitamin D (25-OH), Vitamin B12 (Active), Folate',
      'Magnesium (RBC), Zinc, Selenium, Copper',
      'Iron Panel (Iron, TIBC, UIBC), Ferritin',
      'CoQ10, Vitamin E (Alpha-tocopherol), Vitamin A',
      'Methylmalonic Acid (MMA), Glutathione'
    ],
    status: 'active',
    includedIn: 'Insight OS'
  },
  {
    id: 'organ',
    title: 'Organ Function',
    icon: Shield,
    color: 'blue',
    description: 'Liver, kidney, and metabolic organ health markers',
    biomarkers: [
      'AST, ALT, GGT, ALP, Bilirubin (Total/Direct)',
      'Creatinine, BUN, eGFR (CKD-EPI)',
      'Albumin, Total Protein, Globulin',
      'Uric Acid, Creatine Kinase (CK)',
      'LDH, Amylase, Lipase'
    ],
    status: 'active',
    includedIn: 'Metabolic OS'
  },
  {
    id: 'microbiome',
    title: 'Microbiome Analysis',
    icon: Microscope,
    color: 'amber',
    description: 'Complete gut health and microbiome diversity assessment',
    biomarkers: [
      'Shotgun Metagenomic Sequencing',
      'Gut Diversity & Richness Scores',
      'Pathogen & Parasite Detection',
      'SCFA (Butyrate/Acetate) Production Potential',
      'Probiotic & Prebiotic Precision Recommendations'
    ],
    status: 'coming-soon',
    launchDate: 'Q2 2025',
    includedIn: 'Longevity OS'
  },
  {
    id: 'dna',
    title: 'DNA Optimization',
    icon: Dna,
    color: 'teal',
    description: 'Genetic variants affecting nutrition, fitness, and longevity',
    biomarkers: [
      'MTHFR (Methylation), APOE (Alzheimer\'s Risk)',
      'LPA (Cardiovascular Risk), FTO (Metabolic Risk)',
      'Vitamin Metabolism & Nutrient Absorption Genes',
      'Pharmacogenomics (PGx) Primary Panel',
      'Biological Age (Epigenetic Clock Analysis)'
    ],
    status: 'coming-soon',
    launchDate: 'Q3 2025',
    includedIn: 'Longevity OS'
  },
  {
    id: 'environmental',
    title: 'Environmental Toxins',
    icon: Wind,
    color: 'indigo',
    description: 'Heavy metals, pesticides, and environmental pollutant screening',
    biomarkers: [
      'Mercury, Lead, Arsenic, Cadmium (Blood/Urine)',
      'BPA, Phthalates, Glyphosate Residue',
      'Mycotoxin Panel (Environmental Overgrowth)',
      'Cotinine, Benzene, Toluene',
      'Air Quality & Water Filter Optimization'
    ],
    status: 'coming-soon',
    launchDate: 'Q4 2025',
    includedIn: 'Longevity OS'
  }
];

const TestingModulesSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'active' | 'coming-soon'>('active');

  const filteredModules = testingModules.filter(module => module.status === activeTab);

  const getColorClasses = (color: string, status: string) => {
    if (status === 'coming-soon') {
      return {
        bg: 'glass-surface',
        border: 'border-theme-border dark:border-theme-border-dark',
        icon: 'text-theme-text-muted dark:text-theme-text-muted',
        badge: 'bg-theme-surface-light text-theme-text-primary dark:bg-theme-surface-dark dark:text-theme-text-secondary'
      };
    }

    const colors: Record<string, any> = {
      red: {
        bg: 'glass-surface',
        border: 'border-red-200 dark:border-red-800',
        icon: 'text-red-600 dark:text-red-400',
        badge: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
      },
      purple: {
        bg: 'glass-surface',
        border: 'border-purple-200 dark:border-purple-800',
        icon: 'text-purple-600 dark:text-purple-400',
        badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
      },
      green: {
        bg: 'glass-surface',
        border: 'border-green-200 dark:border-green-800',
        icon: 'text-green-600 dark:text-green-400',
        badge: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
      },
      blue: {
        bg: 'glass-surface',
        border: 'border-blue-200 dark:border-blue-800',
        icon: 'text-blue-600 dark:text-blue-400',
        badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
      },
      amber: {
        bg: 'glass-surface',
        border: 'border-amber-200 dark:border-amber-800',
        icon: 'text-amber-600 dark:text-amber-400',
        badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300'
      },
      teal: {
        bg: 'glass-surface',
        border: 'border-teal-200 dark:border-teal-800',
        icon: 'text-teal-600 dark:text-teal-400',
        badge: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300'
      },
      indigo: {
        bg: 'glass-surface',
        border: 'border-indigo-200 dark:border-indigo-800',
        icon: 'text-indigo-600 dark:text-indigo-400',
        badge: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300'
      }
    };

    return colors[color] || colors.blue;
  };

  return (
    <section className="py-24 bg-theme-bg">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-500 to-teal-400 bg-clip-text text-transparent mb-4">
            Advanced Health Testing Modules
          </h2>
          <p className="text-xl text-theme-secondary max-w-3xl mx-auto">
            Comprehensive biomarker analysis across 8 specialized modules. Everything you need to understand your biology and optimize your health.
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-12">
          <div className="bg-theme-surface/70 rounded-full p-1 inline-flex border border-blue-300/30 dark:border-blue-700/30">
            <button
              onClick={() => setActiveTab('active')}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${
                activeTab === 'active'
                  ? 'bg-gradient-to-r from-blue-600 to-teal-600 text-white shadow-sm'
                  : 'bg-theme-surface/60 text-blue-200 hover:bg-theme-surface/70'
              }`}
            >
              Active Modules
            </button>
            <button
              onClick={() => setActiveTab('coming-soon')}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${
                activeTab === 'coming-soon'
                  ? 'bg-gradient-to-r from-blue-600 to-teal-600 text-white shadow-sm'
                  : 'bg-theme-surface/60 text-blue-200 hover:bg-theme-surface/70'
              }`}
            >
              Coming Soon
            </button>
          </div>
        </div>

        {/* Modules Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {filteredModules.map((module, index) => {
            const Icon = module.icon;
            const colors = getColorClasses(module.color, module.status);
            
            return (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`rounded-xl p-6 border-2 bg-theme-surface/70 ${colors.border} hover:shadow-2xl transform hover:scale-105 group ${
                  module.status === 'coming-soon' ? 'opacity-75' : ''
                }`}
                style={{
                  transition: 'all 300ms ease-in-out',
                  '--hover-shadow': '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                  '--hover-shadow-dark': '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)'
                } as React.CSSProperties}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--hover-shadow)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '';
                }}
                role="article"
                aria-label={`Testing Module: ${module.title}`}
                tabIndex={0}
              >
                <div className="flex items-center justify-between mb-4">
                  <div 
                    className={`w-12 h-12 rounded-xl flex items-center justify-center bg-theme-surface/70 ${colors.icon} transform group-hover:scale-110`}
                    style={{ transition: 'all 300ms ease-in-out' }}
                    aria-hidden="true"
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  {module.status === 'active' ? (
                    <span
                      className="w-3 h-3 rounded-full bg-green-500 border border-green-300 dark:border-green-600"
                      aria-label="Active"
                    />
                  ) : (
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors.badge}`}>
                      {`🚀 ${module.launchDate}`}
                    </span>
                  )}
                </div>
                
                <h3 className="text-lg font-semibold text-theme-primary group-hover:text-blue-400 mb-2">
                  {module.title}
                </h3>
                
                <p className="text-sm text-theme-text-secondary group-hover:text-blue-400 mb-4">
                  {module.description}
                </p>
                
                <div className="space-y-2">
                  {module.biomarkers.map((marker, markerIndex) => (
                    <div key={markerIndex} className="flex items-center space-x-2">
                      <div className={`w-1.5 h-1.5 rounded-full ${colors.icon}`}></div>
                      <span className="text-xs text-theme-secondary">{marker}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Features Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <div className="bg-theme-surface light-surface rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-theme-text mb-6">
              All Modules Include
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-theme-secondary">CLIA Certified Labs</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-theme-secondary">2-3 Day Results</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span className="text-theme-secondary">Physician Reviewed</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span className="text-theme-secondary">AI-Powered Insights</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span className="text-theme-secondary">Trend Tracking</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                <span className="text-theme-secondary">Micro-Sample Option</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestingModulesSection;
